// fade-only sticky sections (desktop ≥1024px)
// - images: cross-fade only
// - text: fade-in/out only
// - first section text is visible before/at first entry; last section image+text latch visible after exit
class FixedSections {
  constructor(containerElement) {
    this.container = { el: containerElement, height: 0, top: 0, bottom: 0, rect: null };
    this.wrapper = this.container.el.querySelector('[data-stick-wrapper]') || this.container.el.firstElementChild;
    this.sections = Array.from(this.wrapper.querySelectorAll(':scope > section'));

    this.viewportTop = 0;
    this.scrollValue = 0;
    this.activeIndex = 0;

    // rAF scroll handler
    this.ticking = false;
    this.boundScrollHandler = () => {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(() => {
          this.onScroll();
          this.ticking = false;
        });
      }
    };

    // direction + latches
    this.prevY = window.scrollY || window.pageYOffset;
    this.scrollDir = 0;            // -1 up, +1 down
    this.lastImageLocked = false;  // latch once last image starts revealing (downward)
    this.lastTextLocked  = false;  // same for last text

    // binds
    this.onScroll   = this.onScroll.bind(this);
    this.onResize   = this.onResize.bind(this);
    this.measure    = this.measure.bind(this);
    this.handle     = this.handle.bind(this);
    this.pinStates  = this.pinStates.bind(this);
    this.remapValue = this.remapValue.bind(this);

    this.init();
  }

  init() {
    this.container.el.style.setProperty('--stick-items', `${this.sections.length}00vh`);
    this.container.el.classList.add('[&_*]:!transition-none');
    this.measure();
    this.handle();
    this.pinStates();

    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.boundScrollHandler, { passive: true });

    setTimeout(() => this.container.el.classList.remove('[&_*]:!transition-none'), 1);
  }

  onResize() {
    this.measure(true);
    this.handle();
    this.pinStates();
  }

  onScroll() {
    this.handle();
    this.pinStates();
  }

  measure(refreshRects = false) {
    if (refreshRects) this.wrapper.style.width = '';
    const rect = this.container.el.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;

    this.viewportTop = scrollY;
    this.container.height = this.container.el.clientHeight;
    this.container.top = scrollY + rect.top;
    this.container.bottom = this.container.top + this.container.height;
    this.container.rect = rect;

    // pinned height
    this.wrapper.style.height = '100vh';
  }

  pinStates() {
    const rect = this.container.el.getBoundingClientRect();
    const containerTopInView = rect.top <= 0;
    const containerBottomPassed = rect.bottom <= window.innerHeight;

    const cs = getComputedStyle(this.container.el);
    const padX = parseFloat(cs.paddingLeft || 0) + parseFloat(cs.paddingRight || 0);
    const width = Math.round(this.container.rect.width - padX);
    const left = Math.round(this.container.rect.left + parseFloat(cs.paddingLeft || 0));

    if (!containerTopInView) {
      this.wrapper.style.position = 'absolute';
      this.wrapper.style.top = '0';
      this.wrapper.style.bottom = 'auto';
      this.wrapper.style.left = '0';
      this.wrapper.style.width = '100%';
    } else if (!containerBottomPassed) {
      this.wrapper.style.position = 'fixed';
      this.wrapper.style.top = '0';
      this.wrapper.style.bottom = 'auto';
      this.wrapper.style.left = `${left}px`;
      this.wrapper.style.width = `${width}px`;
      this.wrapper.style.height = '100vh';
      this.wrapper.style.pointerEvents = 'auto';
      this.wrapper.style.willChange = 'transform';
    } else {
      this.wrapper.style.position = 'absolute';
      this.wrapper.style.top = 'auto';
      this.wrapper.style.bottom = '0';
      this.wrapper.style.left = '0';
      this.wrapper.style.width = '100%';
    }
  }

  ease(t) { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); } // smoothstep

  handle() {
    const y = (this.viewportTop = window.scrollY || window.pageYOffset);

    // scroll direction
    this.scrollDir = (y > this.prevY) ? 1 : (y < this.prevY) ? -1 : this.scrollDir;
    this.prevY = y;

    // map scroll to [0, N+1]
    if (this.container.bottom <= y) {
      this.scrollValue = this.sections.length + 1;
    } else if (this.container.top >= y) {
      this.scrollValue = 0;
    } else {
      this.scrollValue = this.remapValue(
        y, this.container.top, this.container.bottom, 0, this.sections.length + 1
      );
    }

    // indices
    const N = this.sections.length;
    this.scrollValue = Math.max(0, Math.min(this.scrollValue, N));
    const idx = Math.min(N - 1, Math.floor(this.scrollValue));
    const nextIdx = Math.min(N - 1, idx + 1);
    const fRaw = this.scrollValue - Math.floor(this.scrollValue); // 0..1 within step
    const f = Math.max(0, Math.min(1, fRaw));

    const rect = this.container.el.getBoundingClientRect();
    const unpinnedAtBottom = rect.bottom <= window.innerHeight;
    const beforeFirst = (window.scrollY || window.pageYOffset) <= this.container.top;

    // Tunables
    const CROSSFADE_START = 0.70; // when current fades out & next fades in
    const TEXT_FADE_IN = 0.20;    // current text fade-in portion
    const TEXT_FADE_OUT = 0.20;   // current text fade-out portion

    const getParts = (section) => {
      const imgWrap  = section.querySelector('[data-stick-img]')  || section.firstElementChild;
      const textWrap = section.querySelector('[data-stick-text]') || section.lastElementChild;
      return { imgWrap, textWrap };
    };

    // ---------- reset base ----------
    this.sections.forEach((s) => {
      const { imgWrap, textWrap } = getParts(s);
      s.style.pointerEvents = 'none';
      // fade-only baseline
      imgWrap.style.opacity  = '0';
      textWrap.style.opacity = '0';
      imgWrap.style.transform  = 'none';
      textWrap.style.transform = 'none';
      imgWrap.style.willChange  = 'opacity';
      textWrap.style.willChange = 'opacity';
    });

    // ---------- IMAGES: compute opacities with last latch ----------
    const imgOpacity = new Array(N).fill(0);

    if (idx >= 0 && idx < N) {
      const isLast = (idx === N - 1);

      if (isLast) {
        imgOpacity[idx] = 1;
        // scrolling up off the last step resets latch early
        if (this.scrollDir < 0 && f < CROSSFADE_START - 0.02) {
          this.lastImageLocked = false;
        }
      } else {
        if (f < CROSSFADE_START) {
          imgOpacity[idx] = 1;
        } else {
          const t = this.ease((f - CROSSFADE_START) / (1 - CROSSFADE_START));
          imgOpacity[idx] = 1 - t;
          // entering last while scrolling down → latch
          if (this.scrollDir > 0 && idx === N - 2) {
            this.lastImageLocked = true;
          }
        }
      }
    }

    if (idx < N - 1) {
      if (f >= CROSSFADE_START) {
        const t = this.ease((f - CROSSFADE_START) / (1 - CROSSFADE_START));
        imgOpacity[nextIdx] = Math.max(imgOpacity[nextIdx], t);
      }
    }

    if (unpinnedAtBottom || this.lastImageLocked) {
      imgOpacity[N - 1] = 1;
    }

    // first image visible before entering sticky block
    if (beforeFirst) {
      imgOpacity.fill(0);
      imgOpacity[0] = 1;
    }

    // ---------- TEXT: compute opacities (fade-only) with first/last rules ----------
    const textOpacity = new Array(N).fill(0);

    // current text
    if (idx >= 0 && idx < N) {
      let o = 1;

      if (idx === 0) {
        // first section text is fully visible at start; only fade out near end
        if (f > 1 - TEXT_FADE_OUT) {
          o = 1 - this.ease((f - (1 - TEXT_FADE_OUT)) / TEXT_FADE_OUT);
        } else {
          o = 1;
        }
      } else {
        // normal fade in/out
        if (f < TEXT_FADE_IN) {
          o = this.ease(f / TEXT_FADE_IN);
        } else if (f > 1 - TEXT_FADE_OUT) {
          o = 1 - this.ease((f - (1 - TEXT_FADE_OUT)) / TEXT_FADE_OUT);
        } else {
          o = 1;
        }
      }

      textOpacity[idx] = o;

      // last step: keep fully visible
      if (idx === N - 1) {
        textOpacity[idx] = 1;
        if (this.scrollDir < 0 && f < CROSSFADE_START - 0.02) {
          this.lastTextLocked = false;
        }
      }
    }

    // next text fades in during tail
    if (idx < N - 1 && f >= CROSSFADE_START) {
      const t = this.ease((f - CROSSFADE_START) / (1 - CROSSFADE_START));
      textOpacity[nextIdx] = Math.max(textOpacity[nextIdx], t);
      if (this.scrollDir > 0 && idx === N - 2) {
        this.lastTextLocked = true;
      }
    }

    if (unpinnedAtBottom || this.lastTextLocked) {
      textOpacity[N - 1] = 1;
    }

    // first text visible before entering sticky block
    if (beforeFirst) {
      textOpacity.fill(0);
      textOpacity[0] = 1;
    }

    // ---------- apply ----------
    this.sections.forEach((s, i) => {
      const { imgWrap, textWrap } = getParts(s);
      imgWrap.style.opacity  = String(imgOpacity[i].toFixed(3));
      textWrap.style.opacity = String(textOpacity[i].toFixed(3));
      if (imgOpacity[i] > 0.5 || textOpacity[i] > 0.5) {
        s.style.pointerEvents = 'auto';
      }
    });
  }

  remapValue(v, a1, b1, a2, b2) {
    const t = (v - a1) * (b2 - a2) / (b1 - a1) + a2;
    return t > 0 ? t : 0;
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.boundScrollHandler);

    // clear wrapper styles
    this.wrapper.removeAttribute('style');

    // reset section elements to static (for mobile)
    this.sections.forEach((s) => {
      const img = s.querySelector('[data-stick-img]');
      const txt = s.querySelector('[data-stick-text]');
      if (img) {
        img.style.opacity = '1';
        img.style.removeProperty('transform');
        img.style.removeProperty('will-change');
      }
      if (txt) {
        txt.style.opacity = '1';
        txt.style.removeProperty('transform');
        txt.style.removeProperty('will-change');
      }
      s.style.pointerEvents = '';
    });

    this.container.el.style.removeProperty('--stick-items');
    this.container.el.classList.remove('[&_*]:!transition-none');
  }
}

// Desktop-only mount/unmount
const LG = window.matchMedia('(min-width: 1024px)');
let instances = [];

function mount() {
  unmount(); // ensure clean state
  instances = Array.from(document.querySelectorAll('[data-sticky-sections]'))
    .map((el) => new FixedSections(el));
}

function unmount() {
  instances.forEach((i) => i.destroy());
  instances = [];
}

// initial
if (LG.matches) mount();

// respond to MQ change
LG.addEventListener('change', (e) => {
  if (e.matches) mount();
  else unmount();
});

// debounced resize remount (optional, outside MQ)
let _resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(_resizeTimer);
  _resizeTimer = setTimeout(() => {
    if (LG.matches) mount();
    else unmount();
  }, 300);
});
